/**
 * PagePanel - Reusable TipTap-based Panel Component
 * 
 * A single component that renders different page content (Reference Library,
 * Projects, Company Docs, etc.) as panels within the Aerotage interface.
 * 
 * AI-First Design:
 * - RAG-powered semantic search (not keyword search)
 * - Pre-generated AI summaries on document cards
 * - Clickable related document tags from Neptune graph
 * - AI-vision upload flow for new documents
 * - Recent documents on initial load, search replaces with results
 */

import { useState, useEffect, useRef } from 'react';
import { logger } from '../../utils/logger';
import { usePanelSearch } from '../../hooks/usePanelSearch';
import { useFileUpload } from '../../hooks/useFileUpload';
import { usePanelData } from '../../hooks/usePanelData';
import { useAI } from '../../contexts/AIContext';
import type { 
  PagePanelId, 
  ReferenceDocumentCard, 
  ReferenceScopeType,
  MermaidDiagramType,
} from '../../types/panel-types';
import type { ProjectInstance, CompanyDocument, ValidationResult } from '../../types';
import { PAGE_PANEL_CONFIGS } from '../../types/panel-types';
import { 
  projectsApi, 
  validationApi,
  suppliersApi,
} from '../../services/api';
import { ReferenceLibraryContent } from './ReferenceLibraryContent';
import { ProjectsContent } from './ProjectsContent';
import { ProjectDetailContent } from './ProjectDetailContent';
import { CompanyDocsContent } from './CompanyDocsContent';
import { QualityContent } from './QualityContent';
import { SuppliersContent } from './SuppliersContent';
import { CrosswalkContent } from './CrosswalkContent';
import { ValidationContent } from './ValidationContent';
import { DocumentControlContent } from './DocumentControlContent';
import { MyProfileContent } from './MyProfileContent';
import { PreferencesContent } from './PreferencesContent';
import { OrganizationContent } from './OrganizationContent';
import { BillingContent } from './BillingContent';
import { UsersContent } from './UsersContent';
import { ReliabilityAdminContent } from './ReliabilityAdminContent';
import { ProjectEditModal } from '../ProjectEditModal';
import { ProjectCreateModal } from '../ProjectCreateModal';
import { SearchInput } from '../SearchInput';
import ReferenceUploadModal from '../reference/ReferenceUploadModal';
import { useNavigationSafe } from '../../contexts/NavigationContext';
import { Button } from '@/components/ui';

export interface PagePanelProps {
  panelType: PagePanelId;
  searchQuery?: string;
  onOpenDocument: (doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    status?: string;  // Document status for version control
    currentRevision?: string;  // Current revision letter
  }) => void;
  onOpenDiagram?: (diagram: {
    id: string;
    diagramId: string;
    projectId: string;
    title: string;
  }) => void;
  onOpenFlowDiagram?: (diagram: {
    id: string;
    diagramId: string;
    projectId: string;
    title: string;
    mermaidType: MermaidDiagramType;
    svgUrl?: string;
  }) => void;
  onOpenCodingProject?: (codingProject: {
    id: string;
    codingProjectId: string;
    projectId: string;
    title: string;
    projectType: string;
    status: string;
  }) => void;
  onBack?: () => void;  // Navigate back without closing tab
  onOpenWorkspace?: (projectId: string) => void;  // Switch to project workspace
  activeProjectId?: string;  // Auto-select this project when opening projects panel
  autoSelectActiveProject?: boolean;  // If true, auto-select active project immediately (e.g., when closing all tabs)
}

export default function PagePanel({
  panelType,
  searchQuery: initialSearchQuery,
  onOpenDocument,
  onOpenDiagram,
  onOpenFlowDiagram,
  onOpenCodingProject,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBack,
  onOpenWorkspace,
  activeProjectId,
  autoSelectActiveProject = false,
}: PagePanelProps) {
  const config = PAGE_PANEL_CONFIGS[panelType];
  const nav = useNavigationSafe();
  const { activeProject: _activeProject } = useAI();
  
  // Search state (extracted to hook)
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    hasActiveSearch,
  } = usePanelSearch({
    panelType,
    initialSearchQuery,
  });
  
  // Panel data loading hook
  const {
    recentGroups,
    setRecentGroups,
    loadingRecent,
    loadRecentDocuments,
    projects,
    setProjects,
    loadingProjects,
    selectedProject,
    setSelectedProject,
    editingProject,
    setEditingProject,
    loadProjects,
    companyDocs,
    loadingCompanyDocs,
    designDocs,
    loadingDocControl,
    usageMetrics,
    accessLogs,
    loadingQuality,
    suppliers,
    avl,
    loadingSuppliers,
    loadSuppliersData,
    crosswalkEntries,
    crosswalkStandards,
    crosswalkFilterStandard,
    setCrosswalkFilterStandard,
    crosswalkFilterClause,
    setCrosswalkFilterClause,
    loadingCrosswalk,
    loadCrosswalkData,
    error,
  } = usePanelData({ panelType });
  
  // File upload hook - modal-first flow
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [initialUploadFile, setInitialUploadFile] = useState<File | null>(null);
  
  const {
    isDraggingOver,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    clearSelectedFile,
  } = useFileUpload({
    supportsUpload: config.supportsUpload || false,
    onFileSelected: (file) => {
      // When file is selected via drop or file picker, open modal with file attached
      setInitialUploadFile(file);
      setShowUploadModal(true);
    },
  });
  
  // Handle upload modal close
  const handleUploadModalClose = () => {
    setShowUploadModal(false);
    setInitialUploadFile(null);
    clearSelectedFile();
  };
  
  // Handle upload success
  const handleUploadSuccess = async () => {
    setShowUploadModal(false);
    setInitialUploadFile(null);
    clearSelectedFile();
    await loadRecentDocuments();
  };
  
  // Create project modal state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  
  // Validation state (kept local as it's specific to validation panel)
  const [metadataResult, setMetadataResult] = useState<ValidationResult | null>(null);
  const [linksResult, setLinksResult] = useState<ValidationResult | null>(null);
  const [crosswalkResult, setCrosswalkResult] = useState<ValidationResult | null>(null);
  const [allResult, setAllResult] = useState<ValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Handle browser back button for project detail view
  useEffect(() => {
    if (!selectedProject) return;
    
    // Push state when entering project detail
    const state = { panelType, projectId: selectedProject.id };
    window.history.pushState(state, '', window.location.href);
    
    // Handle popstate (browser back button)
    const handlePopState = (event: PopStateEvent) => {
      // If we're in project detail and user clicks back, clear selection
      if (selectedProject) {
        setSelectedProject(null);
        // Prevent default navigation by pushing current state back
        if (!event.state?.projectId) {
          event.preventDefault();
        }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedProject, panelType, setSelectedProject]);
  
  // Track which project ID we last auto-selected for
  const lastAutoSelectedIdRef = useRef<string | null>(null);
  // Track if this is the initial render - don't auto-select on first load
  // so "Browse Projects" shows the list, not project details
  const isInitialRenderRef = useRef(true);
  
  // Auto-select active project when it changes (but NOT on initial render unless autoSelectActiveProject is true)
  useEffect(() => {
    if (panelType === 'projects' && activeProjectId && projects.length > 0) {
      // Skip auto-selection on initial render so "Browse Projects" shows the list
      // UNLESS autoSelectActiveProject is true (e.g., when closing all document tabs)
      if (isInitialRenderRef.current && !autoSelectActiveProject) {
        logger.debug('[PagePanel] Skipping auto-select on initial render - showing project list');
        isInitialRenderRef.current = false;
        return;
      }
      isInitialRenderRef.current = false;
      
      // Only auto-select if this is a different project than we last selected
      if (lastAutoSelectedIdRef.current !== activeProjectId) {
        const project = projects.find(p => p.id === activeProjectId);
        if (project) {
          logger.debug('[PagePanel] Auto-selecting active project:', project.projectCode);
          // Set initial project immediately for UI responsiveness
          setSelectedProject(project);
          lastAutoSelectedIdRef.current = activeProjectId;
          // Fetch full project with documents in background
          fetchProjectWithDocuments(project.id)
            .then(fullProject => {
              setSelectedProject(fullProject);
            })
            .catch(err => {
              logger.error('[PagePanel] Failed to load project documents on auto-select:', err);
            });
        }
      }
    }
  }, [panelType, activeProjectId, projects, setSelectedProject, autoSelectActiveProject]);
  
  // Clear selected project when active project is closed (switched to null)
  useEffect(() => {
    if (panelType === 'projects' && !activeProjectId && selectedProject) {
      logger.debug('[PagePanel] Active project closed, returning to project list');
      setSelectedProject(null);
      lastAutoSelectedIdRef.current = null;
    }
  }, [panelType, activeProjectId, selectedProject, setSelectedProject]);
  
  // Track panel state in navigation context (only on significant changes)
  useEffect(() => {
    nav?.replaceState?.({
      title: config.title,
      panelType,
      searchQuery: searchQuery || undefined,
      extras: selectedProject ? { selectedProjectId: selectedProject.id } : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelType, searchQuery, selectedProject?.id]); // Intentionally exclude nav to prevent loops
  
  // Handle document click - open as tab
  const handleDocumentClick = (doc: ReferenceDocumentCard) => {
    const recentKey = `recent_${panelType}`;
    const savedRecent = localStorage.getItem(recentKey);
    const recentIds = savedRecent ? JSON.parse(savedRecent) : [];
    const updated = [doc.documentId, ...recentIds.filter((id: string) => id !== doc.documentId)].slice(0, 20);
    localStorage.setItem(recentKey, JSON.stringify(updated));
    
    onOpenDocument({
      id: doc.documentId,
      documentNumber: doc.documentId,
      title: doc.standardNumber ? `${doc.standardNumber} - ${doc.title}` : doc.title,
      type: doc.documentType === 'industry-standard' ? 'standard' : 'company',
      canEdit: false,
    });
  };
  
  // Handle related tag click
  const handleRelatedTagClick = (tag: { documentId: string; title: string; standardNumber?: string }) => {
    onOpenDocument({
      id: tag.documentId,
      documentNumber: tag.documentId,
      title: tag.standardNumber ? `${tag.standardNumber} - ${tag.title}` : tag.title,
      type: 'standard',
      canEdit: false,
    });
  };
  
  // Helper to fetch a project with its documents from the DOCUMENTS table
  const fetchProjectWithDocuments = async (projectId: string): Promise<ProjectInstance> => {
    const [project, documents] = await Promise.all([
      projectsApi.get(projectId),
      projectsApi.getDocuments(projectId),
    ]);
    return { ...project, documents: documents || [] };
  };

  // Handle project click - show project detail view AND activate the project
  const handleProjectClick = async (project: ProjectInstance) => {
    // Set initial project immediately for UI responsiveness
    setSelectedProject(project);
    // Also activate this project as the workspace
    if (onOpenWorkspace) {
      onOpenWorkspace(project.id);
    }
    // Fetch full project with documents in background
    try {
      const fullProject = await fetchProjectWithDocuments(project.id);
      setSelectedProject(fullProject);
    } catch (err) {
      logger.error('Failed to load project documents:', err);
    }
  };
  
  // Note: handleBackFromProject removed - user can use "Close Project" dropdown
  
  // Handle company document click
  const handleCompanyDocClick = (doc: CompanyDocument) => {
    // Can only edit Draft or In Review documents (AS9100D compliance)
    const canEdit = doc.status === 'Draft' || doc.status === 'In Review';
    onOpenDocument({
      id: `company-${doc.documentId}`, // Prefix for recent document tracking
      documentNumber: doc.documentId,
      title: doc.title,
      type: 'company',
      canEdit,
      status: doc.status,
      currentRevision: doc.currentRevision,
    });
  };
  
  // Handle document control click
  const handleDocControlClick = (doc: {
    id: string;
    documentNumber: string;
    documentTitle: string;
    projectSpecificTitle?: string;
    projectId?: string;
    projectName?: string;
    projectCode?: string;
    category: 'recent' | 'company' | 'certification' | 'design' | 'shared-design';
    docType?: string;
    documentType?: string;
    currentRevision?: string;
    status?: string;
    standardNumber?: string;
  }) => {
    // Route to appropriate document viewer based on category
    switch (doc.category) {
      case 'company': {
        // Company documents - editable if Draft/In Review
        const canEditCompany = doc.status === 'Draft' || doc.status === 'In Review';
        onOpenDocument({
          id: doc.id, // Keep full ID with prefix for recent tracking
          documentNumber: doc.documentNumber,
          title: doc.documentTitle,
          type: 'company',
          canEdit: canEditCompany,
          status: doc.status,
          currentRevision: doc.currentRevision,
        });
        break;
      }
        
      case 'certification': {
        // Certification documents - editable if Draft/In Review (AS9100D compliance)
        const canEditCert = doc.status === 'Draft' || doc.status === 'In Review';
        onOpenDocument({
          id: doc.id, // Keep full ID with prefix for recent tracking
          documentNumber: doc.documentNumber,
          projectId: doc.projectId,
          title: doc.projectSpecificTitle || doc.documentTitle,
          type: 'project',
          canEdit: canEditCert,
          status: doc.status,
          currentRevision: doc.currentRevision,
        });
        break;
      }
        
      case 'design':
      case 'shared-design':
        // Design documents - stored externally (PDM, Altium, etc.)
        // For now, show as read-only project doc - may need custom viewer later
        onOpenDocument({
          id: doc.id,
          documentNumber: doc.documentNumber,
          projectId: doc.projectId,
          title: doc.documentTitle,
          type: 'project', // Will need 'design' type for proper external file handling
          canEdit: false, // Design docs are externally managed
          status: doc.status,
          currentRevision: doc.currentRevision,
        });
        break;
        
      case 'recent':
        // Recent items - determine type from ID prefix and recurse
        // Only Doc Control scope (no reference docs - those are in Reference Library)
        if (doc.id.startsWith('company-')) {
          handleDocControlClick({ ...doc, category: 'company' });
        } else if (doc.id.startsWith('cert-')) {
          handleDocControlClick({ ...doc, category: 'certification' });
        } else if (doc.id.startsWith('design-')) {
          handleDocControlClick({ ...doc, category: doc.projectId ? 'design' : 'shared-design' });
        }
        break;
    }
  };
  
  // Handle supplier click
  const handleSupplierClick = (supplier: { id: string; name: string }) => {
    // Could open supplier detail modal or panel
    logger.debug('Supplier clicked:', supplier);
  };
  
  // Handle supplier approval
  const handleApproveSupplier = async (supplierId: string) => {
    try {
      await suppliersApi.update(supplierId, { approvalStatus: 'approved' });
      await loadSuppliersData();
    } catch (err) {
      logger.error('Failed to approve supplier:', err);
    }
  };
  
  // Handle supplier rejection
  const handleRejectSupplier = async (supplierId: string) => {
    try {
      await suppliersApi.update(supplierId, { approvalStatus: 'rejected' });
      await loadSuppliersData();
    } catch (err) {
      logger.error('Failed to reject supplier:', err);
    }
  };
  
  // Handle crosswalk filter change
  const handleCrosswalkFilterChange = (standard: string, clause: string) => {
    setCrosswalkFilterStandard(standard);
    setCrosswalkFilterClause(clause);
    loadCrosswalkData();
  };
  
  // Handle crosswalk CSV export
  const handleCrosswalkExport = () => {
    let csv = 'Document,Standard,Clauses\n';
    crosswalkEntries.forEach(entry => {
      if (entry.standards) {
        Object.entries(entry.standards).forEach(([std, clauses]) => {
          csv += `"${entry.path}","${std}","${(clauses as string[]).join(', ')}"\n`;
        });
      }
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crosswalk.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  // Handle validation run
  const handleRunValidation = async (type: 'metadata' | 'links' | 'crosswalk' | 'all') => {
    setValidating(true);
    setValidationError(null);
    
    try {
      const projectsList = await projectsApi.list();
      
      if (!projectsList || projectsList.length === 0) {
        setValidationError('No projects found. Create a project first to run validation.');
        setValidating(false);
        return;
      }
      
      const projectId = projectsList[0].id;
      
      interface BackendValidation {
        check: string;
        status: string;
        issues?: string[];
      }
      interface BackendResult {
        validations?: BackendValidation[];
      }
      const convertBackendResult = (backendResult: BackendResult, checkType: string): ValidationResult => {
        const validation = backendResult.validations?.find((v) => v.check === checkType);
        if (!validation) {
          return { passed: false, errors: ['Validation check not found'], warnings: [] };
        }
        return {
          passed: validation.status === 'pass',
          errors: validation.status === 'fail' ? (validation.issues || []) : [],
          warnings: validation.status === 'warning' ? (validation.issues || []) : []
        };
      };
      
      switch (type) {
        case 'metadata': {
          const metaResult = await validationApi.validateMetadata(projectId);
          setMetadataResult(convertBackendResult(metaResult, 'metadata'));
          break;
        }
        case 'links': {
          const linkResult = await validationApi.validateLinks(projectId);
          setLinksResult(convertBackendResult(linkResult, 'links'));
          break;
        }
        case 'crosswalk': {
          const cwResult = await validationApi.validateCrosswalk(projectId);
          setCrosswalkResult(convertBackendResult(cwResult, 'crosswalk'));
          break;
        }
        case 'all': {
          const allRes = await validationApi.validateAll(projectId);
          setAllResult(convertBackendResult(allRes, 'all'));
          // Also set individual results
          setMetadataResult(convertBackendResult(allRes, 'metadata'));
          setLinksResult(convertBackendResult(allRes, 'links'));
          setCrosswalkResult(convertBackendResult(allRes, 'crosswalk'));
          break;
        }
      }
    } catch (err) {
      setValidationError(`Validation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setValidating(false);
    }
  };
  
  const toggleGroupExpanded = (scopeType: ReferenceScopeType) => {
    setRecentGroups(prev => prev.map(g => 
      g.scopeType === scopeType ? { ...g, isExpanded: !g.isExpanded } : g
    ));
  };
  
  // Determine what to show: search results or recent documents
  // Use explicit flag instead of deriving from query - prevents race conditions
  const showSearchResults = hasActiveSearch || isSearching;
  
  // Render panel content based on type
  const renderPanelContent = () => {
    switch (panelType) {
      case 'reference-library':
        return (
          <ReferenceLibraryContent
            showSearchResults={showSearchResults}
            searchResults={searchResults}
            recentGroups={recentGroups}
            loading={loadingRecent}
            isSearching={isSearching}
            onDocumentClick={handleDocumentClick}
            onRelatedTagClick={handleRelatedTagClick}
            onToggleGroup={toggleGroupExpanded}
            onRefresh={loadRecentDocuments}
          />
        );
      
      case 'projects':
        // Show project detail if a project is selected
        if (selectedProject) {
          return (
            <ProjectDetailContent
              project={selectedProject}
              onOpenDocument={onOpenDocument}
              onOpenDiagram={onOpenDiagram}
              onOpenFlowDiagram={onOpenFlowDiagram}
              onOpenCodingProject={onOpenCodingProject}
              onEditProject={() => setEditingProject(selectedProject)}
              onRefreshProject={async () => {
                // Reload projects list AND update selected project with fresh data including documents
                await loadProjects();
                try {
                  const freshProject = await fetchProjectWithDocuments(selectedProject.id);
                  setSelectedProject(freshProject);
                } catch (err) {
                  logger.error('Failed to refresh selected project:', err);
                }
              }}
              onDeleteProject={() => {
                // Clear selection and reload projects list after deletion
                setSelectedProject(null);
                loadProjects();
              }}
            />
          );
        }
        return (
          <ProjectsContent
            projects={projects}
            loading={loadingProjects}
            isSearching={isSearching}
            searchQuery={searchQuery}
            onProjectClick={handleProjectClick}
            onCreateProject={() => setShowCreateProjectModal(true)}
            onRefreshProjects={loadProjects}
          />
        );
      
      case 'company-docs':
        return (
          <CompanyDocsContent
            documents={companyDocs}
            loading={loadingCompanyDocs}
            isSearching={isSearching}
            searchQuery={searchQuery}
            onDocumentClick={handleCompanyDocClick}
          />
        );
      
      case 'quality':
        return (
          <QualityContent
            projects={projects}
            usageMetrics={usageMetrics}
            accessLogs={accessLogs}
            loading={loadingQuality}
          />
        );
      
      case 'suppliers':
        return (
          <SuppliersContent
            suppliers={suppliers}
            avl={avl}
            loading={loadingSuppliers}
            isSearching={isSearching}
            searchQuery={searchQuery}
            onSupplierClick={handleSupplierClick}
            onApprove={handleApproveSupplier}
            onReject={handleRejectSupplier}
            onCreateSupplier={() => {
              logger.debug('Create supplier clicked');
            }}
          />
        );
      
      case 'reliability-admin':
        return (
          <ReliabilityAdminContent
            loading={false}
            isSearching={isSearching}
            searchQuery={searchQuery}
          />
        );
      
      case 'crosswalk':
        return (
          <CrosswalkContent
            entries={crosswalkEntries}
            standards={crosswalkStandards}
            loading={loadingCrosswalk}
            isSearching={isSearching}
            searchQuery={searchQuery}
            filterStandard={crosswalkFilterStandard}
            filterClause={crosswalkFilterClause}
            onFilterChange={handleCrosswalkFilterChange}
            onExportCSV={handleCrosswalkExport}
          />
        );
      
      case 'validation':
        return (
          <ValidationContent
            metadataResult={metadataResult}
            linksResult={linksResult}
            crosswalkResult={crosswalkResult}
            allResult={allResult}
            validating={validating}
            error={validationError}
            onRunValidation={handleRunValidation}
          />
        );
      
      case 'doc-control':
        return (
          <DocumentControlContent
            projects={projects}
            designDocs={designDocs}
            companyDocs={companyDocs}
            loading={loadingDocControl}
            isSearching={isSearching}
            searchQuery={searchQuery}
            onDocumentClick={handleDocControlClick}
          />
        );
      
      case 'my-profile':
        return <MyProfileContent />;
      
      case 'preferences':
        return <PreferencesContent />;
      
      case 'organization':
        return <OrganizationContent />;
      
      case 'billing':
        return <BillingContent />;
      
      case 'users':
        return <UsersContent />;
      
      default:
        return (
          <div className="p-8 text-center text-muted-foreground">
            <div className="text-4xl mb-4">{config.icon}</div>
            <p>{config.title} panel coming soon</p>
          </div>
        );
    }
  };
  
  return (
    <div 
      className="h-full flex flex-col bg-background"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header with search and upload - hidden when viewing project details (tab already shows project name) */}
      {!(panelType === 'projects' && selectedProject) && (
        <div className={`flex-shrink-0 ${
          isDraggingOver ? 'bg-info-light' : 'bg-background'
        }`}>
          {/* Title row - matches DocumentViewerContent styling */}
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm">{config.icon}</span>
              <h2 className="text-sm font-medium text-foreground">
                {config.title}
              </h2>
            </div>
            {config.supportsUpload && (
              <>
                {/* Hidden file input for drag-and-drop file selection */}
                {/* eslint-disable-next-line no-restricted-syntax -- file inputs require native element */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.xlsx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                  className="hidden"
                  onChange={handleFileInputChange}
                  aria-label="Select file to upload"
                />
                {/* Upload button opens modal directly (modal has its own drop zone) */}
                <Button
                  size="sm"
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2"
                  aria-label="Upload document"
                >
                  <span aria-hidden="true">+</span>
                  <span>Upload</span>
                </Button>
              </>
            )}
          </div>
          
          {/* Search bar */}
          {config.supportsSearch && (
            <div className="px-4 pb-2" role="search" aria-label={config.searchPlaceholder || 'Search this panel'}>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={config.searchPlaceholder}
                isLoading={isSearching}
              />
            </div>
          )}
          
          {/* Drop zone indicator */}
          {isDraggingOver && config.supportsUpload && (
            <div className="mx-4 mb-2 p-4 border-2 border-dashed border-primary rounded-lg bg-info-light text-center">
              <span className="text-info">
                Drop file here to upload (PDF, DOCX, XLSX, TXT)
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Error message */}
        {error && (
          <div role="alert" className="m-4 p-3 bg-destructive-light border border-destructive rounded-lg text-destructive text-sm">
            {error}
          </div>
        )}
        
        {/* Panel content */}
        {renderPanelContent()}
      </div>
      
      {/* Project Edit Modal */}
      {editingProject && (
        <ProjectEditModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={(updated) => {
            // Update the selected project with new data
            setSelectedProject(updated);
            // Update in projects list
            setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingProject(null);
          }}
        />
      )}
      
      {/* Project Create Modal */}
      {showCreateProjectModal && (
        <ProjectCreateModal
          onClose={() => setShowCreateProjectModal(false)}
          onCreated={(newProject) => {
            // Add to projects list
            setProjects(prev => [newProject, ...prev]);
            // Select the new project
            setSelectedProject(newProject);
            setShowCreateProjectModal(false);
            // Activate this project as the workspace
            if (onOpenWorkspace) {
              onOpenWorkspace(newProject.id);
            }
          }}
        />
      )}
      
      {/* Reference Upload Modal (modal-first flow) */}
      {showUploadModal && (
        <ReferenceUploadModal
          open={showUploadModal}
          onClose={handleUploadModalClose}
          onSuccess={handleUploadSuccess}
          initialFile={initialUploadFile}
          customerId={_activeProject?.customerId}
        />
      )}
    </div>
  );
}
